import React, {useCallback} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import {styled, Stack} from '@mui/material';
import translate from '@/utils/translate';
import {PREVIEW_CREATOR_ID_QUERY_PARAM} from '@/config/routesConstants';
import Sidebar from '@/components/sidebar';
import useCampaignV2CreatorsCountsQuery from '@/hooks/queries/use-campaign-v2-creators-counts-query';
import useBrandId from '@/hooks/use-brand-id';
import styles from './CampaignCreatorsTab.module.scss';
import PaidGigCreatorsPane from './PaidGigCreatorsPane';
import InvitedCreatorsPane from './components/invited-creators-pane';
import {
  CREATOR_STATUS,
  isPaidGigCreatorStatus,
  type CreatorStatus,
} from './types/creatorTabTypes';

const CreatorsTabContainer = styled(Stack)({
  flexDirection: 'row',
});

const VALID_STATUSES = new Set<string>(Object.values(CREATOR_STATUS));
const DEFAULT_STATUS = CREATOR_STATUS.Applications;

function isValidStatus(value: string | null): value is CreatorStatus {
  return value !== null && VALID_STATUSES.has(value);
}

function CampaignCreatorsTab() {
  const brandId = useBrandId();
  const {campaignId} = useParams<{campaignId: string}>();
  const [searchParams, setSearchParams] = useSearchParams();
  const statusParam = searchParams.get('creatorStatus');
  const {counts, isFetched: isCountsFetched} = useCampaignV2CreatorsCountsQuery(
    {
      brandId,
      campaignId,
    }
  );
  // When the URL doesn't specify a tab and the campaign is past the
  // applications phase (no pending applicants but some accepted creators),
  // default to the Accepted tab so the user lands on the work that's still
  // active. An explicit tab in the URL always wins, so deep links are honored.
  const isPastApplicationsPhase =
    isCountsFetched &&
    (counts.applications ?? 0) === 0 &&
    (counts.hired ?? 0) > 0;
  const fallbackStatus = isPastApplicationsPhase
    ? CREATOR_STATUS.Hired
    : DEFAULT_STATUS;
  const activeStatus: CreatorStatus = isValidStatus(statusParam)
    ? statusParam
    : fallbackStatus;
  const isInvitedStatus = activeStatus === CREATOR_STATUS.Invited;

  const handleStatusSelect = useCallback(
    (id: string) => {
      if (!isValidStatus(id)) {
        return;
      }
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('creatorStatus', id);
          if (id !== CREATOR_STATUS.Applications) {
            next.delete('shortlisted');
          }
          if (id === CREATOR_STATUS.Invited) {
            next.delete(PREVIEW_CREATOR_ID_QUERY_PARAM);
          }
          return next;
        },
        {replace: true}
      );
    },
    [setSearchParams]
  );

  const statusItems = [
    {
      id: CREATOR_STATUS.Applications,
      label: translate('Applications'),
      count: counts.applications ?? 0,
      hasUnread: counts.unreadApplications ?? false,
    },
    {
      id: CREATOR_STATUS.Hired,
      label: translate('Accepted'),
      count: counts.hired ?? 0,
      hasUnread: counts.unreadHired ?? false,
    },
    {
      id: CREATOR_STATUS.Declined,
      label: translate('Declined'),
      count: counts.declined ?? 0,
      hasUnread: counts.unreadDeclined ?? false,
    },
    {
      id: CREATOR_STATUS.Invited,
      label: translate('Invited'),
      count: counts.invited ?? 0,
    },
  ];

  return (
    <CreatorsTabContainer className={styles.container}>
      <Sidebar
        title={translate('Status')}
        items={statusItems}
        selectedId={activeStatus}
        onSelect={handleStatusSelect}
      />
      {isInvitedStatus ? (
        <InvitedCreatorsPane />
      ) : (
        <PaidGigCreatorsPane
          activeStatus={
            isPaidGigCreatorStatus(activeStatus)
              ? activeStatus
              : CREATOR_STATUS.Applications
          }
        />
      )}
    </CreatorsTabContainer>
  );
}

export default CampaignCreatorsTab;
